import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/** The body scroll lock is shared by every open dialog: how many hold it, and the scroll position to return to. */
const scrollLock = { holders: 0, scrollY: 0 };

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    // `cancel` and `close` don't bubble, so they're caught on the way down. Listening here rather than on the
    // dialog also covers a dialog element replaced by a section re-render.
    this.addEventListener('cancel', this.#handleCancel, { capture: true });
    this.addEventListener('close', this.#handleClose, { capture: true });

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('cancel', this.#handleCancel, { capture: true });
    this.removeEventListener('close', this.#handleClose, { capture: true });

    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }

    // Removed while open (a section re-render, say): don't leave the page locked.
    this.#unlockScroll();
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /** Whether this dialog holds the body scroll lock, set from showDialog() until the dialog closes. */
  #isScrollLocked = false;

  /** @type {Promise<void> | null} */
  #closing = null;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      if (dialog.open) return;

      this.#lockScroll(scrollY);

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog. A call made while the closing animation runs waits for that one.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.#closing ??= this.#animateClose(dialog).finally(() => {
      this.#closing = null;
    });

    return this.#closing;
  };

  /**
   * Plays the closing animation, then closes the dialog.
   *
   * @param {HTMLDialogElement} dialog - The dialog element.
   */
  async #animateClose(dialog) {
    this.#removeOpenListeners();

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    // Closed natively during the animation: #handleClose has already released the page.
    if (!dialog.open) {
      dialog.classList.remove('dialog-closing');
      return;
    }

    this.#unlockScroll();

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Fixes the body in place so the page behind the dialog can't scroll.
   *
   * @param {number} scrollY - The scroll position to hold.
   */
  #lockScroll(scrollY) {
    if (this.#isScrollLocked) return;

    this.#isScrollLocked = true;

    // Another dialog already fixed the body (quick add handing over to the cart drawer, say): scrollY reads 0
    // now, so keep the position the first dialog saved.
    if (scrollLock.holders++ > 0) return;

    scrollLock.scrollY = scrollY;
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
  }

  /**
   * Releases this dialog's hold on the body lock. The last dialog out unfixes the body and returns to the
   * scroll position it held. Does nothing when this dialog holds no lock.
   */
  #unlockScroll() {
    if (!this.#isScrollLocked) return;

    this.#isScrollLocked = false;

    if (--scrollLock.holders > 0) return;

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: scrollLock.scrollY, behavior: 'instant' });
  }

  #removeOpenListeners() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
  }

  /**
   * Runs the closing animation for a close the browser starts itself (Escape, Android's back gesture).
   *
   * @param {Event} event - The cancel event.
   */
  #handleCancel = (event) => {
    if (event.target !== this.refs.dialog) return;

    // A cancel without recent user activation can't be prevented; #handleClose then releases the page.
    if (!event.cancelable) return;

    event.preventDefault();
    this.closeDialog();
  };

  /**
   * Releases the page after a close that skipped closeDialog(): dialog.close() called elsewhere, a
   * form method="dialog", or a cancel that couldn't be prevented.
   *
   * @param {Event} event - The close event.
   */
  #handleClose = (event) => {
    const { dialog } = this.refs;

    if (event.target !== dialog || dialog.open || !this.#isScrollLocked) return;

    this.#removeOpenListeners();
    this.#unlockScroll();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

/**
 * The open `<details scroll-lock>` elements. The page stays locked while one of them is open and rendered, so the
 * lock is released when one is removed or hidden while open, not only when it closes.
 *
 * @type {Set<HTMLDetailsElement>}
 */
const scrollLockDetails = new Set();

/** Watches for removal and resizing while a scroll-lock details element is open. @type {AbortController | null} */
let scrollLockWatch = null;

function updateDetailsScrollLock() {
  for (const details of scrollLockDetails) {
    if (!details.isConnected || !details.open) scrollLockDetails.delete(details);
  }

  const locked = [...scrollLockDetails].some((details) => details.getClientRects().length > 0);
  document.documentElement.toggleAttribute('scroll-lock', locked);

  if (scrollLockDetails.size && !scrollLockWatch) {
    scrollLockWatch = new AbortController();

    const observer = new MutationObserver(updateDetailsScrollLock);
    observer.observe(document.body, { childList: true, subtree: true });
    scrollLockWatch.signal.addEventListener('abort', () => observer.disconnect());

    window.addEventListener('resize', updateDetailsScrollLockOnResize, { signal: scrollLockWatch.signal });
  } else if (!scrollLockDetails.size && scrollLockWatch) {
    scrollLockWatch.abort();
    scrollLockWatch = null;
  }
}

const updateDetailsScrollLockOnResize = debounce(updateDetailsScrollLock, 50);

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        if (event.target.open) {
          scrollLockDetails.add(event.target);
        } else {
          scrollLockDetails.delete(event.target);
        }

        updateDetailsScrollLock();
      }
    }
  },
  { capture: true }
);
